'use client'

import { useRouter } from 'nextjs-toploader/app'
import { clsx } from 'shared/libs/style-merge'

import { Image } from 'shared/ui'
import { Button } from 'shared/ui/button'
import { EmptyStateType } from 'shared/ui/empty/types'

import { ROUTER_PATHS, RouterPath } from 'shared/config/routerPath/RouterPath'

import styles from './Empty.module.scss'

interface EmptyStateData {
  title: string
  image: string
  desc: string
  url: string
  link: string
}

const emptyStateData: Record<EmptyStateType, EmptyStateData> = {
  [EmptyStateType.ADDRESSES_EMPTY]: {
    title: 'У вас пока нет адресов для доставки',
    image: '/static/png/empty_7.png',
    desc: 'Адреса для доставки нужны только для курьеров',
    url: '',
    link: '',
  },
  [EmptyStateType.CART_EMPTY]: {
    title: 'В корзине нет товаров',
    image: '/static/png/empty_2.png',
    desc: 'Добавляйте себе самые интересные товары по лучшим скидкам',
    url: RouterPath.CATEGORY,
    link: 'В каталог!',
  },
  [EmptyStateType.CATALOG_EMPTY_BY_CATEGORY]: {
    title: 'Товаров по данной категории нет',
    image: '/static/png/empty_1.png',
    desc: 'Но есть много новых интересных товаров в каталоге',
    url: RouterPath.CATEGORY,
    link: 'В каталог!',
  },
  [EmptyStateType.CATALOG_EMPTY_BY_FILTERS]: {
    title: 'Товаров по таким фильтрам нет',
    image: '/static/png/empty_1.png',
    desc: 'Но есть много новых интересных товаров в каталоге',
    url: RouterPath.CATALOG,
    link: 'Сбросить фильтры',
  },
  [EmptyStateType.CATALOG_EMPTY_BY_SEARCH]: {
    title: 'Мы искали но не нашли',
    image: '/static/png/empty_5.png',
    desc: 'Попробуйте написать по-другому или поискать сразу в каталоге',
    url: RouterPath.CATEGORY,
    link: 'В каталог!',
  },

  [EmptyStateType.ACTIVE_ORDERS_EMPTY]: {
    title: 'У вас пока нет заказов',
    image: '/static/png/empty_3.png',
    desc: 'Здесь будет история ваших заказов',
    url: RouterPath.CATEGORY,
    link: 'В каталог!',
  },
  [EmptyStateType.ORDERS_EMPTY]: {
    title: 'У вас пока не было заказов',
    image: '/static/png/empty_4.png',
    desc: 'Здесь будет история ваших завершенных заказов',
    url: RouterPath.CATEGORY,
    link: 'В каталог!',
  },
  [EmptyStateType.FAVORITES_EMPTY]: {
    title: 'У вас пока нет товаров в избранном',
    image: '/static/png/empty_6.png',
    desc: 'Сохраняйте товары которые больше всего вам понравились',
    url: RouterPath.CATEGORY,
    link: 'В каталог!',
  },
  [EmptyStateType.PROFILE_EMPTY]: {
    title: 'Войдите в профиль',
    image: '/static/png/cart.png',
    desc: 'Чтобы начать заказывать товары, добавлять в избранное',
    url: ROUTER_PATHS.auth,
    link: 'Войти!',
  },
  [EmptyStateType.PRODUCT_EMPTY]: {
    title: 'По данному запросу, нет товара',
    image: '/static/png/empty_2.png',
    desc: 'Попробуйте выбрать другой товар',
    url: ROUTER_PATHS.home,
    link: 'На главную',
  },
  [EmptyStateType.FORM_SUCCESS]: {
    title: 'Ваш запрос отправлен',
    image: '/static/png/empty_2.png',
    desc: 'Скоро наш менеджер свяжется с вами',
    url: ROUTER_PATHS.home,
    link: 'На главную',
  },
  [EmptyStateType.NO_CONNECTION]: {
    title: 'Нет интернета',
    image: '/static/png/empty_2.png',
    desc: 'Похоже, у вас пропало соединение. Проверьте подключение к сети и попробуйте снова.',
    url: ROUTER_PATHS.home,
    link: 'На главную',
  },
  [EmptyStateType.NOT_FOUND]: {
    title: 'Нет такой страницы',
    image: '/static/png/empty_2.png',
    desc: 'К сожалению, мы не нашли то, что вы искали. Попробуйте начать с главной страницы.',
    url: ROUTER_PATHS.home,
    link: 'На главную',
  },
  [EmptyStateType.NOT_FOUND_CATEGORY]: {
    title: 'Нет такой категории',
    image: '/static/png/empty_2.png',
    desc: 'Попробуйте выбрать другую категорию',
    url: ROUTER_PATHS.category,
    link: 'В каталог',
  },
  [EmptyStateType.NOT_FOUND_SHOP]: {
    title: 'Нет такого магазина',
    image: '/static/png/empty_2.png',
    desc: 'Попробуйте выбрать другой магазин',
    url: ROUTER_PATHS.category,
    link: 'В каталог',
  },
  [EmptyStateType.RETURNS_EMPTY]: {
    title: 'Тут будут ваши заявки на возврат',
    image: '/static/png/empty_2.png',
    desc: 'Иногда бывает, что товар не подошел. Мы всегда за то, чтобы покупки вас радовали.',
    url: ROUTER_PATHS.category,
    link: 'В каталог',
  },
}

interface EmptyProps {
  variant: EmptyStateType
  hasButton?: boolean
  imageWidth?: number
  imageHeight?: number
  containerClassName?: string
  showImage?: boolean
}

export function Empty({
  variant,
  hasButton = true,
  showImage = true,
  imageHeight = 180,
  imageWidth = 180,
  containerClassName,
}: EmptyProps) {
  const router = useRouter()

  return (
    <div className={clsx(styles.container, containerClassName)}>
      <div className={styles.wrapper}>
        {showImage && (
          <Image
            src={emptyStateData[variant]?.image}
            width={imageWidth}
            height={imageHeight}
            alt="/"
          />
        )}
        <h4 className={styles.title}>{emptyStateData[variant]?.title}</h4>
        <p className={styles.desc}>{emptyStateData[variant]?.desc}</p>
        {hasButton && (
          <Button
            className={styles.button}
            onClick={() => router.push(emptyStateData[variant]?.url)}
            variant="primary"
          >
            {emptyStateData[variant]?.link}
          </Button>
        )}
      </div>
    </div>
  )
}
